import React, { useState } from "react";
import { Checkbox } from "primereact/checkbox";

import { setChargingNetwork } from "../../../../services/eroundProvisioning";
import { Decision, ProvisioningDetail } from "../../../../types/eroundProvisioning";
import { actorLabel } from "../../../../utils/actors";
import { provisioningErrorMessage } from "./provisioningErrors";

// The Ladeverbund mark can change until the station is on its way; a failed one can be retried (SOPS-174).
const MARKABLE = ["COLLECTING", "AWAITING_APPROVAL", "FAILED"];

/** The station carries more charging points than the eRound model it maps to. */
export const exceedsModel = (record: ProvisioningDetail): boolean => {
  const points = record.station?.chargingPoints ?? null;
  return !!record.eroundMapping && points !== null && points > record.eroundMapping.chargingPoints;
};

/** Offered where it applies, and wherever it is already set so it can be taken back. */
export const offersLadeverbund = (record: ProvisioningDetail): boolean =>
  exceedsModel(record) || !!record.chargingNetwork;

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleString();
};

export const markedBy = (mark: Decision): string =>
  `${actorLabel(mark.actorEmail ?? mark.actorId) || mark.actorId}, ${formatDate(mark.at)}`;

interface LadeverbundToggleProps {
  record: ProvisioningDetail;
  // Re-reads the record; the station moves on by itself, e.g. to Ready for approval.
  onChanged: () => Promise<unknown> | void;
}

/**
 * Marks a station as a Ladeverbund (master/slave): one station in eRound with all its charging
 * points on the customer's model (SOPS-174). In the stations table, so support needs no dialog.
 */
const LadeverbundToggle: React.FC<LadeverbundToggleProps> = ({ record, onChanged }) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!offersLadeverbund(record)) return null;

  const mark = record.chargingNetwork ?? null;
  const points = record.station?.chargingPoints;
  const inputId = `ladeverbund-${record.stationId}`;
  const explanation =
    `One station in eRound with all ${points} charging points on this model.` +
    (mark ? ` Marked by ${markedBy(mark)}.` : "");

  const change = async (enabled: boolean) => {
    setPending(true);
    setError(null);
    try {
      await setChargingNetwork(record.stationId, enabled);
      await onChanged();
    } catch (err) {
      setError(provisioningErrorMessage(err, "ladeverbund"));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mt-1" title={explanation}>
      <div className="flex align-items-center gap-2">
        <Checkbox
          inputId={inputId}
          checked={mark !== null}
          disabled={pending || !MARKABLE.includes(record.status)}
          onChange={(e) => change(Boolean(e.checked))}
        />
        <label htmlFor={inputId}>Ladeverbund (master/slave)</label>
      </div>
      {error && <small className="p-error block">{error}</small>}
    </div>
  );
};

export default LadeverbundToggle;
